import { ColDef } from "ag-grid-community";
import { observer } from "mobx-react";
import * as React from "react";
import Localization from "../core/Localization";
import RequestPromise from "../core/RequestPromise";
import { useWidth, WidthProps } from "../core/Responsive";
import Sys from "../core/Sys";
import ComboBoxOption from "../coreui/ComboBoxOption";
import FocusTracker from "../coreui/FocusTracker";
import Table, { TableChildProps, TableProps } from "../coreui/Table";
import AsyncData, {
  GetDataResponse,
  LoadingState,
} from "../coreui/table/AsyncData";
import PaneRow from "../models/PaneRow";
import RoundTripService from "../services/RoundTripService";
import PaneDataStore from "../stores/PaneDataStore";
import { CardControl } from "./CardControl";
import GridColumn, { GridColumnConfigProperties } from "./Columns/GridColumn";
import ErrorBoundary from "./ErrorBoundary";
import { RuntimeProperties, SelectChildProps } from "./SelectControl";

interface ConfigProperties {
  cardDepth: number;
  columns: GridColumnConfigProperties[];
  contentDataId: string;
  data?: object;
  dataId: string;
  description: string;
  footerToolbar?: object;
  headerToolbar?: object;
  name: string;
  propagated?: SelectChildProps;
  selectionToolbar?: object;
  tableKey: string;
  verticalLayout?: object;
}

export class SelectControlSelectedGrid extends React.Component<
  ConfigProperties & WidthProps
> {
  private asyncDataRef: React.RefObject<AsyncData>;
  private readonly contentDataId: string;
  private readonly dataId: string;
  private focusTrackerRef: React.RefObject<FocusTracker>;
  private loadingHasBeenNotified: boolean = false;
  private readonly name: string;
  private populate: ((rows: PaneRow[]) => void) | null = null;
  private propagated: TableChildProps & SelectChildProps;

  public constructor(props: ConfigProperties & WidthProps) {
    super(props);

    this.asyncDataRef = React.createRef();
    this.focusTrackerRef = React.createRef();

    this.contentDataId = props.contentDataId;
    this.dataId = props.dataId;
    this.name = props.name;

    this.propagated = {
      parentTable: {
        cardDepth: props.cardDepth,
        columns: props.columns,
        configProps: {
          contentDataId: props.contentDataId,
          data: props.data,
          dataId: props.dataId,
          name: props.name,
        },
        description: props.description,
        getRelationshipComboBoxOptions: this.getRelationshipComboBoxOptions,
        isDocumentGrid: false,
        populateData: () => this.populateData(),
      },
      ...props.propagated,
    } as TableChildProps & SelectChildProps;
  }

  private announceLoadingComplete = (): void => {
    setTimeout(() => {
      Sys.announce(
        Localization.getBuiltInMessage("DataTable.loadComplete", {
          gridDescription: this.props.description,
        }),
        true
      );
    }, 1000);
  };

  private announceLoadingStarted = (): void => {
    Sys.announce(
      Localization.getBuiltInMessage("DataTable.loadStarted", {
        gridDescription: this.props.description,
      }),
      true
    );
  };

  private getAccessibleDescription(rowCount: number): string {
    if (rowCount === 0) {
      return Localization.getBuiltInMessage("DataTable.tableRowCountZero");
    }

    if (rowCount === 1) {
      return Localization.getBuiltInMessage("DataTable.tableRowCountSingle");
    }

    return Localization.getBuiltInMessage("DataTable.tableRowCountMultiple", {
      count: rowCount,
    });
  }

  private getData = (): RequestPromise<GetDataResponse> => {
    const row = PaneRow.get(this.dataId)!;

    return RoundTripService.partialDataRetrevial<GetDataResponse>(
      `SelectControl/GetRowsData/${row.rowKey}/${this.dataId}/${this.name}`
    );
  };

  private getRelationshipComboBoxOptions = (
    widgetName: string,
    selectedOption: ComboBoxOption
  ): ComboBoxOption[] => {
    // Relationship combo boxes are always read-only on select controls,
    // since the only details that can be maintained are relationship
    // details, which are never mainted with a relationship combo box.
    return [selectedOption];
  };

  private onFocusChanged = (isFocused: boolean): void => {
    if (!this.asyncDataRef) {
      return;
    }

    if (isFocused) {
      const loadingState: LoadingState =
        this.asyncDataRef.current!.getLoadingState();
      if (loadingState.isLoadingData || loadingState.isPopulatingData) {
        this.announceLoadingStarted();
      }
    } else {
      this.loadingHasBeenNotified = false;
    }
  };

  private onIsLoadingChanged = (
    isLoadingData: boolean,
    isPopulatingData: boolean
  ): void => {
    this.props.propagated!.parentSelect.onIsLoadingChanged(isLoadingData);

    if (!this.focusTrackerRef) {
      return;
    }

    if (!this.focusTrackerRef.current?.isFocused()) {
      return;
    }

    if (isLoadingData || isPopulatingData) {
      if (!this.loadingHasBeenNotified) {
        this.announceLoadingStarted();
        this.loadingHasBeenNotified = true;
      }
    } else {
      this.announceLoadingComplete();
      this.loadingHasBeenNotified = false;
    }
  };

  private populateData = (): void => {
    const rows: PaneRow[] = PaneDataStore.getPaneCollection(this.contentDataId);

    if (this.populate !== null) {
      this.populate(rows);
    }
  };

  public componentDidUpdate(prevProps: ConfigProperties & WidthProps): void {
    if (prevProps.width !== this.props.width) {
      setTimeout(() => this.populateData());
    }
  }

  public render(): React.ReactNode {
    const row = PaneRow.get(this.props.dataId);
    const widget = row!.getWidgetT<null, RuntimeProperties>(this.props.name);

    const tableColumns: ColDef[] = [];
    for (const column of this.props.columns) {
      tableColumns.push(
        GridColumn.getColumnDef(column, this.props.columns, this.propagated)
      );
    }

    const tableProps: TableProps = {
      "aria-label": this.props.description,
      cardDepth: this.props.cardDepth,
      cellEdit: true,
      columns: tableColumns,
      contentDataId: this.props.contentDataId,
      dataId: this.props.dataId,
      footerToolbarChild: this.props.footerToolbar,
      getAccessibleDescription: this.getAccessibleDescription,
      headerToolbarChild: this.props.headerToolbar,
      isColumnFlex: (colId: string) =>
        GridColumn.isColumnFlex(this.props.columns, colId),
      isColumnVisible: (colId: string, breakpoint: string) =>
        GridColumn.isColumnVisible(this.props.columns, colId, breakpoint),
      minRowHeight: GridColumn.getColumnsMinRowHeight(this.props.columns),
      name: this.props.name,
      propagated: this.propagated,
      rowSelection: this.propagated?.parentSelect?.configProps?.isSingleSelect
        ? "single"
        : "multiple",
      selectToolbarChild: this.props.selectionToolbar,
      setPopulate: (populate) => (this.populate = populate),
    };

    return (
      <FocusTracker
        onFocusChanged={this.onFocusChanged}
        ref={this.focusTrackerRef}
      >
        <ErrorBoundary title={this.props.name}>
          <div style={{ position: "relative" }}>
            <AsyncData
              contentDataId={this.props.contentDataId}
              dataId={this.props.dataId}
              getData={this.getData}
              onIsLoadingChanged={this.onIsLoadingChanged}
              populateData={this.populateData}
              ref={this.asyncDataRef}
            />
            {this.props.verticalLayout ? (
              <CardControl
                cardConfig={this.props.verticalLayout}
                cardDepth={this.props.cardDepth}
                columns={this.props.columns}
                contentDataId={this.props.contentDataId}
                description={this.props.description}
                errors={widget.properties.businessErrors}
                footerToolbarConfig={this.props.footerToolbar}
                getAccessibleDescription={this.getAccessibleDescription}
                headerToolbarConfig={this.props.headerToolbar}
                propagated={this.propagated}
                selectionToolbarConfig={this.props.selectionToolbar}
                tableKey={this.props.tableKey}
              />
            ) : (
              <Table {...tableProps} tableKey={this.props.tableKey} />
            )}
          </div>
        </ErrorBoundary>
      </FocusTracker>
    );
  }
}

// FUTURE
// This wrapper component was created to avoid the scope of converting this
// component to a hooks component during the MUI 5 upgrade. When the legacy
// component is converted to a hooks component, this wrapper can be removed and
// hooks it calls can be called by the converted component directly.
const Observer = observer(SelectControlSelectedGrid);
export default function Wrapped(props: ConfigProperties): JSX.Element {
  const width = useWidth();
  return <Observer {...props} width={width} />;
}
